<template>
  <HrPageLayout
    title="Unused Leave Approvals"
    subtitle="Director HR · Welfare · HR Admin queues and reports"
    header-tone="institutional"
    :breadcrumb-items="[
      { label: 'HR', route: '/dashboard' },
      { label: 'Unused Leave Approvals' }
    ]"
  >
    <div v-if="!canDirector && !canWelfare && !canAdmin" class="ula-warn">
      You do not have an approver role for this module. Required permissions:
      <strong>UNUSED LEAVE APPROVE DIRECTOR HR</strong>,
      <strong>UNUSED LEAVE APPROVE WELFARE</strong>, or
      <strong>UNUSED LEAVE ADMIN / MANAGE LEAVE</strong>.
    </div>

    <template v-else>
      <div class="ula-tabs" role="tablist">
        <button
          v-if="canDirector"
          type="button"
          class="ula-tab"
          :class="{ on: tab === 'director' }"
          @click="setTab('director')"
        >
          <i class="bx bx-user-check"></i>
          Director HR queue
          <span v-if="tab === 'director'" class="count">{{ claims.length }}</span>
        </button>
        <button
          v-if="canWelfare"
          type="button"
          class="ula-tab"
          :class="{ on: tab === 'welfare' }"
          @click="setTab('welfare')"
        >
          <i class="bx bx-heart"></i>
          Welfare queue
          <span v-if="tab === 'welfare'" class="count">{{ claims.length }}</span>
        </button>
        <button
          v-if="canAdmin"
          type="button"
          class="ula-tab"
          :class="{ on: tab === 'admin' }"
          @click="setTab('admin')"
        >
          <i class="bx bx-list-ul"></i>
          All submissions
          <span v-if="tab === 'admin'" class="count">{{ claims.length }}</span>
        </button>
      </div>

      <div class="ula-metrics">
        <div class="ula-metric">
          <span class="lbl">In view</span>
          <strong>{{ claims.length }}</strong>
        </div>
        <div class="ula-metric">
          <span class="lbl">Pending</span>
          <strong>{{ pendingCount }}</strong>
        </div>
        <div class="ula-metric">
          <span class="lbl">Approved</span>
          <strong class="ok">{{ approvedCount }}</strong>
        </div>
        <div class="ula-metric">
          <span class="lbl">Rejected</span>
          <strong class="bad">{{ rejectedCount }}</strong>
        </div>
      </div>

      <div class="ula-card">
        <div class="ula-toolbar">
          <div class="ula-search">
            <i class="bx bx-search"></i>
            <input
              v-model="search"
              type="search"
              placeholder="Search employee, type, status, ID…"
              class="ula-input"
            />
          </div>
          <div class="ula-actions">
            <button type="button" class="hr-btn hr-btn-secondary" :disabled="loading" @click="load">
              <i class="bx bx-refresh"></i> {{ loading ? "Loading…" : "Refresh" }}
            </button>
            <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="!filteredClaims.length || exporting" @click="exportCsv">
              <i class="bx bx-export"></i> CSV
            </button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="!filteredClaims.length || exporting" @click="exportPdf">
              <i class="bx bx-file"></i> {{ exporting ? "Exporting…" : "PDF report" }}
            </button>
          </div>
        </div>

        <div v-if="loading" class="ula-empty">
          <div class="hr-spinner"></div>
          <p>Loading claims…</p>
        </div>
        <div v-else-if="!filteredClaims.length" class="ula-empty">
          <i class="bx bx-inbox ula-empty-icon"></i>
          <h4>{{ claims.length ? "No matches" : "No records in this queue" }}</h4>
          <p>{{ claims.length ? "Try a different search." : "Claims will appear here once submitted." }}</p>
        </div>
        <div v-else class="ula-table-wrap">
          <table class="ula-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Employee</th>
                <th>Leave type</th>
                <th class="center">Days</th>
                <th>Year</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Remarks</th>
                <th class="center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in filteredClaims" :key="c.id">
                <td class="mono">#{{ c.id }}</td>
                <td>
                  <div class="emp">{{ c.employeeNames }}</div>
                  <div class="sub">{{ c.nationalId }}</div>
                </td>
                <td>{{ c.leaveTypeName }}</td>
                <td class="center mono">{{ c.daysClaimed }}</td>
                <td>{{ c.year || "—" }}</td>
                <td>
                  <span class="ula-badge" :class="badgeClass(c.status)">{{ formatStatus(c.status) }}</span>
                </td>
                <td class="sub">{{ formatDate(c.createdAt) }}</td>
                <td class="remarks" :title="c.remarks || ''">{{ shortText(c.remarks) }}</td>
                <td class="center actions">
                  <template v-if="tab === 'director' && c.status === 'pending_director'">
                    <button type="button" class="act ok" @click="openAct(c, 'approve_director')">Approve</button>
                    <button type="button" class="act bad" @click="openAct(c, 'reject_director')">Reject</button>
                  </template>
                  <template v-else-if="tab === 'welfare' && c.status === 'pending_welfare'">
                    <button type="button" class="act ok" @click="openAct(c, 'approve_welfare')">Approve</button>
                    <button type="button" class="act bad" @click="openAct(c, 'reject_welfare')">Reject</button>
                  </template>
                  <template v-else-if="tab === 'admin'">
                    <button type="button" class="act ok" @click="openAct(c, 'hr_force_approve')">Force approve</button>
                    <button type="button" class="act bad" @click="openAct(c, 'hr_force_reject')">Force reject</button>
                  </template>
                  <span v-else class="muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <div v-if="modal.open" class="ula-overlay" @click.self="modal.open = false">
      <div class="ula-modal" role="dialog" aria-modal="true">
        <header>
          <h3>{{ modalTitle }}</h3>
          <p class="muted">
            Claim #{{ modal.claim?.id }} — {{ modal.claim?.employeeNames }} ·
            {{ modal.claim?.leaveTypeName }} ({{ modal.claim?.daysClaimed }} days)
          </p>
        </header>
        <label class="lbl">Comments / reason</label>
        <textarea
          v-model="modal.remarks"
          class="ula-input ta"
          rows="3"
          :placeholder="remarksPlaceholder"
        ></textarea>
        <footer>
          <button type="button" class="hr-btn hr-btn-secondary" :disabled="modal.saving" @click="modal.open = false">
            Cancel
          </button>
          <button
            type="button"
            class="hr-btn"
            :class="modal.action.includes('reject') ? 'hr-btn-danger' : 'hr-btn-primary'"
            :disabled="modal.saving"
            @click="confirmAct"
          >
            {{ modal.saving ? "Saving…" : "Confirm" }}
          </button>
        </footer>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight, toast } from "@/api";
import { downloadCsv, downloadCountyTablePdf } from "@/utils/countyReportExport";

export default {
  name: "UnusedLeaveApprovals",
  components: { HrPageLayout },
  data() {
    return {
      tab: "director",
      claims: [],
      loading: false,
      exporting: false,
      search: "",
      modal: { open: false, claim: null, action: "", remarks: "", saving: false }
    };
  },
  computed: {
    canDirector() {
      return getRight("UNUSED LEAVE APPROVE DIRECTOR HR");
    },
    canWelfare() {
      return getRight("UNUSED LEAVE APPROVE WELFARE");
    },
    canAdmin() {
      return getRight(["UNUSED LEAVE ADMIN", "MANAGE LEAVE"]);
    },
    modalTitle() {
      const a = this.modal.action || "";
      if (a.includes("reject")) return "Reject claim";
      if (a.includes("approve")) return "Approve claim";
      return "Process claim";
    },
    remarksPlaceholder() {
      return this.modal.action.includes("reject")
        ? "Required: reason for rejection"
        : "Optional: notes for the employee / next approver";
    },
    filteredClaims() {
      const q = String(this.search || "")
        .trim()
        .toLowerCase();
      if (!q) return this.claims;
      return this.claims.filter((c) => {
        const hay = [
          c.id,
          c.employeeNames,
          c.nationalId,
          c.leaveTypeName,
          c.status,
          c.remarks,
          c.year
        ]
          .map((x) => String(x || "").toLowerCase())
          .join(" ");
        return hay.includes(q);
      });
    },
    pendingCount() {
      return this.claims.filter((c) => String(c.status || "").startsWith("pending")).length;
    },
    approvedCount() {
      return this.claims.filter((c) => String(c.status || "").toLowerCase() === "approved").length;
    },
    rejectedCount() {
      return this.claims.filter((c) => String(c.status || "").toLowerCase() === "rejected").length;
    },
    queueLabel() {
      if (this.tab === "director") return "Director HR queue";
      if (this.tab === "welfare") return "Welfare queue";
      return "All submissions";
    }
  },
  mounted() {
    if (this.canDirector) this.tab = "director";
    else if (this.canWelfare) this.tab = "welfare";
    else if (this.canAdmin) this.tab = "admin";
    this.load();
  },
  methods: {
    setTab(t) {
      this.tab = t;
      this.search = "";
      this.load();
    },
    shortText(s) {
      const t = String(s || "");
      return t.length > 80 ? `${t.slice(0, 77)}…` : t || "—";
    },
    formatStatus(s) {
      return String(s || "—")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    badgeClass(s) {
      const v = String(s || "").toLowerCase();
      if (v === "approved") return "ok";
      if (v === "rejected") return "bad";
      if (v.includes("pending")) return "warn";
      return "";
    },
    formatDate(v) {
      if (!v) return "—";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v).slice(0, 16);
      return d.toLocaleString();
    },
    async load() {
      if (!this.canDirector && !this.canWelfare && !this.canAdmin) return;
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "listUnusedLeaveClaims");
        fd.append("view", this.tab);
        const res = await execute(fd, authUrl);
        if (res.data.success) this.claims = res.data.data?.claims || [];
        else {
          this.claims = [];
          toast.error(res.data.message || "Failed to load claims");
        }
      } catch (e) {
        this.claims = [];
        toast.error(e?.response?.data?.message || "Failed to load claims");
      } finally {
        this.loading = false;
      }
    },
    openAct(claim, action) {
      this.modal = { open: true, claim, action, remarks: "", saving: false };
    },
    async confirmAct() {
      if (!this.modal.claim) return;
      if (String(this.modal.remarks).trim().length < 3 && String(this.modal.action).includes("reject")) {
        toast.error("Please enter at least 3 characters for the rejection reason.");
        return;
      }
      this.modal.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "processUnusedLeaveClaim");
        fd.append("claimId", String(this.modal.claim.id));
        fd.append("action", this.modal.action);
        fd.append("remarks", String(this.modal.remarks).trim());
        const res = await execute(fd, authUrl);
        if (!res.data.success) {
          toast.error(res.data.message || "Failed");
          return;
        }
        toast.success(res.data.message || "Done");
        this.modal.open = false;
        await this.load();
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Error");
      } finally {
        this.modal.saving = false;
      }
    },
    reportRows() {
      return this.filteredClaims.map((c) => [
        c.id,
        c.employeeNames || "",
        c.nationalId || "",
        c.leaveTypeName || "",
        c.daysClaimed ?? "",
        c.year ?? "",
        this.formatStatus(c.status),
        this.formatDate(c.createdAt),
        c.directorApprovedBy || "",
        c.welfareApprovedBy || "",
        c.remarks || "",
        c.rejectionReason || ""
      ]);
    },
    exportCsv() {
      if (!this.filteredClaims.length) return;
      const headers = [
        "ID",
        "Employee",
        "National ID",
        "Leave Type",
        "Days",
        "Year",
        "Status",
        "Submitted",
        "Director Approver",
        "Welfare Approver",
        "Remarks",
        "Rejection Reason"
      ];
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(headers, this.reportRows(), `unused_leave_${this.tab}_${stamp}.csv`);
      toast.success("CSV downloaded");
    },
    async exportPdf() {
      if (!this.filteredClaims.length) return;
      this.exporting = true;
      try {
        const headers = [
          "ID",
          "Employee",
          "National ID",
          "Leave Type",
          "Days",
          "Year",
          "Status",
          "Submitted",
          "Director",
          "Welfare",
          "Remarks",
          "Rejection"
        ];
        const stamp = new Date().toISOString().slice(0, 10);
        await downloadCountyTablePdf({
          title: "UNUSED LEAVE CLAIMS REPORT",
          subtitle: this.queueLabel,
          periodLabel: `Generated ${stamp} · ${this.filteredClaims.length} record(s)`,
          headers,
          rows: this.reportRows(),
          filename: `unused_leave_${this.tab}_${stamp}.pdf`,
          orientation: "landscape"
        });
        toast.success("PDF report generated");
      } catch (e) {
        toast.error(e?.message || "PDF export failed");
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.ula-warn {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #9f1239;
  border-radius: 12px;
  padding: 14px 16px;
  line-height: 1.5;
}
.ula-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.ula-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  padding: 9px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  color: #334155;
}
.ula-tab i {
  font-size: 16px;
}
.ula-tab.on {
  background: #0c2d48;
  color: #fff;
  border-color: #0c2d48;
}
.ula-tab .count {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 11px;
}
.ula-tab:not(.on) .count {
  background: #e2e8f0;
  color: #0f172a;
}
.ula-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.ula-metric {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
}
.ula-metric .lbl {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}
.ula-metric strong {
  font-size: 22px;
  color: #0c2d48;
}
.ula-metric strong.ok {
  color: #15803d;
}
.ula-metric strong.bad {
  color: #b91c1c;
}
.ula-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px 18px;
}
.ula-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.ula-search {
  position: relative;
  flex: 1 1 240px;
  max-width: 420px;
}
.ula-search i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}
.ula-search .ula-input {
  padding-left: 32px;
  width: 100%;
}
.ula-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ula-input {
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 9px 11px;
  font-size: 13px;
  background: #fff;
}
.ula-input.ta {
  width: 100%;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}
.ula-empty {
  text-align: center;
  padding: 40px 12px;
  color: #64748b;
}
.ula-empty-icon {
  font-size: 40px;
  color: #94a3b8;
}
.ula-empty h4 {
  margin: 8px 0 4px;
  color: #0f172a;
}
.ula-table-wrap {
  overflow: auto;
  max-height: 620px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.ula-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.ula-table th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  text-align: left;
  padding: 10px 8px;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
  white-space: nowrap;
  z-index: 1;
}
.ula-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}
.ula-table .center {
  text-align: center;
}
.ula-table .mono {
  font-variant-numeric: tabular-nums;
}
.emp {
  font-weight: 600;
  color: #0f172a;
}
.sub {
  font-size: 11px;
  color: #64748b;
}
.remarks {
  max-width: 180px;
  color: #64748b;
  font-size: 12px;
}
.ula-badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: #f1f5f9;
  color: #475569;
  white-space: nowrap;
}
.ula-badge.warn {
  background: #fff7ed;
  color: #c2410c;
}
.ula-badge.ok {
  background: #ecfdf5;
  color: #047857;
}
.ula-badge.bad {
  background: #fef2f2;
  color: #b91c1c;
}
.actions {
  white-space: nowrap;
}
.act {
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
  padding: 2px 4px;
  margin: 0 2px;
}
.act.ok {
  color: #15803d;
}
.act.bad {
  color: #b91c1c;
}
.muted {
  color: #94a3b8;
}
.ula-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  padding: 16px;
}
.ula-modal {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  width: min(440px, 100%);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}
.ula-modal header h3 {
  margin: 0 0 4px;
  color: #0c2d48;
}
.ula-modal header p {
  margin: 0 0 12px;
  font-size: 13px;
}
.ula-modal .lbl {
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 4px;
  color: #475569;
}
.ula-modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}
@media (max-width: 800px) {
  .ula-metrics {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
